// import jwt from "jsonwebtoken";
let refreshTimeout = null;
let isIdleExpired = false; // 標記是否已經閒置逾時
let isLoggingOut = false; // 標記是否正在登出（避免重複彈窗）
const JWT_EXPIRY_TIME = 300 * 60 * 1000; // 30 分鐘 (可修改: 如 60 * 60 * 1000 = 1小時)

const setRefreshTimeout = (onExpire) => {
  clearTimeout(refreshTimeout);
  refreshTimeout = setTimeout(onExpire, JWT_EXPIRY_TIME);
};

const clearRefreshTimeout = () => {
  clearTimeout(refreshTimeout);
};

const refreshJWT = async () => {
  try {
    const oldToken = localStorage.getItem("rw_app_tk"); // 使用 AuthManager 的 token key
    if (!oldToken) return;

    // 直接呼叫 API，避免循環依賴
    const response = await fetch(
      `${
        process.env.REACT_APP_LOGIN_API_URL
      }/Common/RefreshToken?oldToken=${encodeURIComponent(oldToken)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response.ok) {
      const result = await response.json();
      if (result.Success && result.Data) {
        // 更新 localStorage 中的 token
        const newToken = result.Data.startsWith("Bearer ")
          ? result.Data
          : `Bearer ${result.Data}`;
        localStorage.setItem("rw_app_tk", newToken);
        // console.log("✅ Token 刷新成功");

        // 動態更新 APIService 的 token（如果存在）
        try {
          const apiService = await import("../services/APIService");
          if (apiService.default?.updateApiKey) {
            apiService.default.updateApiKey(result.Data);
          }
        } catch (error) {
          // console.log("📝 APIService 尚未創建，跳過更新");
        }
      }
    }
  } catch (error) {
    // console.error("❌ 刷新 token 失敗:", error);
  }
};

const onIdleExpire = () => {
  //console.log("⏰ 使用者閒置逾時，標記為已過期");
  isIdleExpired = true; // 只標記過期，不立即登出
};

const checkAndLogoutIfExpired = () => {
  if (isIdleExpired && !isLoggingOut) {
    // 檢查當前是否在會員相關頁面
    const currentPath = window.location.pathname;
    const isMemberPage =
      currentPath.includes("/member/") ||
      currentPath.includes("/Login") ||
      currentPath === "/ChangePassword";

    if (!isMemberPage) {
      // console.log("⚠️ 非會員頁面，跳過逾時登出檢查");
      return false; // 不是會員頁面，不執行登出
    }

    isLoggingOut = true; // 標記為正在登出，避免重複執行
    // console.log("🚪 會員頁面偵測到閒置逾時，執行登出");

    // 清除所有認證相關資料
    const keys = ["rw_app_tk", "rw_usr_prof", "rw_acc_rem"];
    keys.forEach((key) => localStorage.removeItem(key));

    alert("會員閒置時間過長，請重新登入");
    const homePage = process.env.REACT_APP_HOME_PAGE || "";
    window.location.href = `${homePage}/Login`; // 導向登入頁
    return true;
  }
  return false;
};

export const startIdleTimer = () => {
  isIdleExpired = false;
  // const startTime = new Date().toLocaleTimeString();
  // console.log(`🕐 [${startTime}] 開始 JWT 閒置計時器 (2分鐘)`);
  setRefreshTimeout(onIdleExpire);
};

export const resetIdleTimer = () => {
  // 先檢查是否已經閒置逾時
  if (checkAndLogoutIfExpired()) {
    return; // 如果已過期則登出，不重置計時器
  }

  clearRefreshTimeout();
  isIdleExpired = false;
  // console.log("🔄 重置 JWT 閒置計時器");
  setRefreshTimeout(onIdleExpire);
};

export const refreshJWTIfNeeded = async () => {
  // 先檢查是否閒置逾時（只在會員頁面才執行登出）
  if (checkAndLogoutIfExpired()) {
    return; // 如果已過期則登出
  }

  const token = localStorage.getItem("rw_app_tk");
  if (!token) return;

  try {
    // 移除 "Bearer " 前綴（如果有）
    const rawToken = token.replace(/^Bearer\s+/i, "");

    // 解析 JWT payload
    const parts = rawToken.split(".");
    if (parts.length !== 3) {
      // console.log("⚠️ Token 不是標準 JWT 格式，跳過自動刷新");
      return;
    }

    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);

    // console.log("🔍 JWT 刷新檢查:", {
    //   exp: payload.exp,
    //   now: now,
    //   timeLeft: payload.exp ? payload.exp - now : "N/A",
    // });

    // 檢查 JWT 是否快過期（在 5 分鐘內過期）
    if (payload.exp && payload.exp - now < 300) {
      // console.log("Token 即將過期，執行刷新");
      await refreshJWT();
    }
  } catch (error) {
    // console.error("檢查或刷新 token 時發生錯誤:", error);
    // console.log("⚠️ Token 可能不是 JWT 格式，跳過自動刷新");
    // 如果解析失敗，可能 token 格式有問題，但不強制刷新
  }
};

/**
 * 檢查 JWT 是否已過期
 * @returns {boolean} 是否已過期
 */
export const isJWTExpired = () => {
  try {
    const token = localStorage.getItem("rw_app_tk");
    if (!token) return true;

    const rawToken = token.replace(/^Bearer\s+/i, "");
    const parts = rawToken.split(".");
    if (parts.length < 2) return true;

    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);

    return payload.exp && payload.exp < now;
  } catch (error) {
    return true;
  }
};

/**
 * 強制登出（清除所有資料並導向登入頁）
 */
export const forceLogout = () => {
  if (isLoggingOut) return; // 避免重複執行

  isLoggingOut = true;
  clearRefreshTimeout();

  // 清除所有認證相關資料
  const keys = ["rw_app_tk", "rw_usr_prof", "rw_acc_rem"];
  keys.forEach((key) => localStorage.removeItem(key));

  // console.log("🚪 強制登出");
  const homePage = process.env.REACT_APP_HOME_PAGE || "";
  window.location.href = `${homePage}/Login`;
};

/**
 * 停止閒置計時器（離開會員頁面時使用）
 */
export const stopIdleTimer = () => {
  clearRefreshTimeout();
  isIdleExpired = false;
  // console.log("⏹️ 停止 JWT 閒置計時器");
};
